import { RichTextEditor, RichTextEditorProps } from '@mantine/tiptap';
import '@mantine/tiptap/styles.css';
import { Button } from '@zorro/design-system/v2/Button';
import { SendIcon } from '@zorro/shared/assets';
import { Box, Group, RichTextEditorLink } from '@zorro/web/design-system';
import { KeyboardEvent } from 'react';

type RichTextCommentEditorProps = Pick<RichTextEditorProps, 'editor'> & {
  onMouseLeave?: () => void;
  onClick?: () => void;
  isEdit?: boolean;
  onCancel?: () => void;
  content?: string;
  viewport?: React.RefObject<HTMLDivElement | null>;
  sendButtonAlign?: 'bottom' | 'center';
};

export const RichTextCommentEditor = ({
  editor,
  onMouseLeave,
  onClick,
  onCancel,
  isEdit = false,
  content,
  viewport,
  sendButtonAlign = 'bottom',
}: RichTextCommentEditorProps) => {
  const isEmptyContent =
    !content || content === '{}' || !editor?.getText().trim();

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      if (!isEmptyContent) {
        onClick?.();
      }
    }
  };

  const sendButton = (
    <Button
      onClick={onClick}
      disabled={isEmptyContent}
      size="sm"
      leftSection={<SendIcon disabled={isEmptyContent} />}
      aria-label="Send comment"
    />
  );

  return (
    <RichTextEditor
      editor={editor}
      w="100%"
      variant="subtle"
      style={{
        ...(!isEdit && { border: 'none' }),
      }}
      bg="white"
      ref={viewport}
      onKeyDown={handleKeyDown}
    >
      <Box pos="relative">
        <RichTextEditor.Content
          onMouseLeave={onMouseLeave}
          style={{
            maxHeight: 300,
            overflowY: 'auto',
          }}
        />
        {!isEdit && sendButtonAlign === 'center' && (
          <Box
            pos="absolute"
            right="0.375rem"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            {sendButton}
          </Box>
        )}
      </Box>
      <Group justify="space-between" pos="relative">
        {!editor?.isEmpty && (
          <RichTextEditor.Toolbar
            sticky
            stickyOffset={60}
            style={{ border: 'none' }}
          >
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
              <RichTextEditorLink />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>
        )}
        {isEdit ? (
          <Group mr="sm" mb="sm" flex={1} justify="end">
            <Button onClick={onCancel} variant="secondary" size="sm">
              Cancel
            </Button>
            <Button onClick={onClick} size="sm" disabled={isEmptyContent}>
              Save
            </Button>
          </Group>
        ) : (
          sendButtonAlign === 'bottom' && (
            <Box pos="absolute" right="0.375rem" bottom="0.375rem">
              {sendButton}
            </Box>
          )
        )}
      </Group>
    </RichTextEditor>
  );
};
